import React from 'react';
import $ from 'jquery';
import ConversationApi from './api/ConversationApi';
import * as CorgeeHelper from '../utils/CorgeeHelper';
import ActionMenuConversation from './ActionMenuConversation';
import ProfilePictureModal from './ProfilePictureModal';
import RenderInBody from '../Globals/RenderInBody';
import './ConversationCardHeader.css';
import { storage } from '@lib/storage';
import { buildChatWindowUrl, getChatBaseUrl } from '@lib/chatUrl';
import type { ConversationDetail, Reply } from '../../types/conversation';
import type { ApiConfig, AppConfig, CorgeeFunctions } from './types';
import type { ConversionResult } from './modals/ConvertToModal';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import { setCardBeingDragged, getCurrentDashboardId } from '@/lib/cardDragState';
import { conversationBus } from '@/lib/conversationBus';
import { useNavigate } from 'react-router-dom';

type NavigateFn = ReturnType<typeof useNavigate>;

declare global {
  interface Window {
    chatWebReact: Window | null;
  }
}

interface ConversationCardHeaderProps {
  conversation: ConversationDetail;
  api: ApiConfig;
  config: AppConfig;
  onUpdateConversation?: (
    conversationId: number,
    updates: Partial<ConversationDetail>,
    skipUpdate?: boolean,
    source?: string
  ) => void;
  isInfowindowViewCard?: boolean;
  isConversationDetailsView?: boolean;
  isInfowindowDrag?: boolean;
  onHighlight?: boolean;
  SocketConnectionId?: string;
  corgeeFunctions?: CorgeeFunctions;
  userData?: {
    personName: string;
  };
  storageData?: {
    enableChat?: boolean;
    personid?: number;
  };
  chatData?: {
    enableChat?: boolean;
    personid?: number;
  };
  reply?: Reply;
  hideProjectName?: boolean;
  isHomeView?: boolean;
  onSubmitTime?: (conversation?: any) => void;
  onAddReply?: (
    conversationId: number,
    reply: any,
    sendUpdate?: boolean,
    updateTotalReplies?: boolean
  ) => void;
  columnWidth?: number;
  forceShowDetails?: boolean;
  hasArchivedFilter?: boolean;
  navigate?: NavigateFn;
}

interface ConversationCardHeaderState {
  isImportant: boolean | null;
  linkCopied: boolean;
  finished: boolean;
  title: string;
  titleOriginal: string;
  updatedBy: string;
  updatedAt: string;
  isRedFlag: boolean;
  icon: string;
  enableEditTitle: boolean;
  enableEditBody: boolean;
  displayFullContent: boolean;
  textHeight: number;
  body: string;
  bodyEdited: string;
  showSeeMoreLink: boolean;
  showBodyIsRequired: boolean;
  firstLayerColor: string;
  openProfilePictureDialog: boolean;
  userData: Record<string, any>;
  positionProfilePictureY: number;
  positionProfilePictureX: number;
  canOpenChat: boolean;
  reqNewChat: {
    Participant1?: number;
    Participant2?: number;
    ChatID?: number;
  };
  isGroupDefault: boolean | null;
  isFavorite: boolean;
  isRecycleBin: boolean;
}

interface NewChatRequest {
  Participant1: number;
  Participant2: number;
  ChatID: number;
}

interface UpdateTitleRequest {
  ConversationId: number;
  Title: string;
  SocketConnectionId?: string;
  ConfigValueID: number | null;
}

interface UpdateCompleteRequest {
  DashboardGroupId: number;
  ConversationId: number;
  Finished: boolean;
  SocketConnectionId?: string;
}

interface ConversationUpdatePayload {
  ConfigValueId: number;
  ConversationId: number;
  CoreTaskId: number | null;
  CreatedByID: number;
  Finished: boolean;
  Icon: string;
  IsRedFlag?: boolean;
  IsImportant?: boolean;
  ProjectId: number;
  SideMenuText: string;
  Title: string;
  UpdatedAt: string;
  callService: boolean;
}

interface UpdateRedFlagsRequest {
  conversations: ConversationUpdatePayload[];
  SocketConnectionId?: string;
}

interface UpdateImportantRequest {
  conversations: ConversationUpdatePayload[];
  SocketConnectionId?: string;
}

interface ChatResponse {
  ChatID?: number;
}

class ConversationCardHeader extends React.Component<
  ConversationCardHeaderProps,
  ConversationCardHeaderState
>
{
  private profilePictureItem: any = null;
  private spanTitleRef: HTMLSpanElement | null = null;
  private _dragGhost: HTMLElement | null = null;
  private inputTitleRef: HTMLInputElement | null = null;
  private _titleClickHandler: EventListener | null = null;
  private _unsubRedflag: (() => void) | null = null;
  private _unsubCompleted: (() => void) | null = null;

  static getDerivedStateFromProps(
    props: ConversationCardHeaderProps,
    state: ConversationCardHeaderState,
  ): Partial<ConversationCardHeaderState> | null
  {
    // Keep state.icon in sync with props when it changes (e.g. background cache refresh or Convert-To)
    if (props.conversation.Icon !== undefined && props.conversation.Icon !== state.icon)
    {
      return { icon: props.conversation.Icon };
    }
    return null;
  }

  constructor(props: ConversationCardHeaderProps)
  {
    super(props);

    const layers =
      props.conversation.ConversationLayers && props.conversation.ConversationLayers.length > 0
        ? props.conversation.ConversationLayers.filter(x => x.LayerId !== 0)
        : [];
    const isRecycleBin = window.location.href.indexOf('RecycleBin') > -1;

    this.state = {
      isImportant: props.conversation.IsImportant,
      linkCopied: false,
      finished: props.conversation.Finished,
      title: props.conversation.Title,
      titleOriginal: props.conversation.Title,
      updatedBy: props.conversation.UpdatedBy,
      updatedAt: props.conversation.UpdatedAt,
      isRedFlag: props.conversation.IsRedFlag,
      icon: props.conversation.Icon,
      enableEditTitle: false,
      enableEditBody: false,
      displayFullContent:
        props.isConversationDetailsView === undefined ? false : props.isConversationDetailsView,
      textHeight: 150,
      body: props.conversation.Body,
      bodyEdited: props.conversation.Body,
      showSeeMoreLink: false,
      showBodyIsRequired: false,
      firstLayerColor:
        layers && layers.length > 0 ? (layers[0]?.LayerColor ? layers[0].LayerColor : '') : '',
      openProfilePictureDialog: false,
      userData: {},
      positionProfilePictureY: 0,
      positionProfilePictureX: 0,
      canOpenChat: false,
      reqNewChat: {},
      isGroupDefault: null,
      isFavorite: props.conversation?.IsFavorite || false,
      isRecycleBin: isRecycleBin,
    };

    this.onCopyToClipboard = this.onCopyToClipboard.bind(this);
    this.copyToClipboard = this.copyToClipboard.bind(this);
    this.onChangeFinished = this.onChangeFinished.bind(this);
    this.onEditTitle = this.onEditTitle.bind(this);
    this.onMarkAsRedFlag = this.onMarkAsRedFlag.bind(this);
    this.onMarkAsImportant = this.onMarkAsImportant.bind(this);
    this.onSendToBookmarks = this.onSendToBookmarks.bind(this);
    this.getClickHandler = this.getClickHandler.bind(this);
    this.enableInputClickEvent = this.enableInputClickEvent.bind(this);
    this.onSeeMoreContent = this.onSeeMoreContent.bind(this);
    this.onSaveTitle = this.onSaveTitle.bind(this);
    this.onChangeTitle = this.onChangeTitle.bind(this);
    this.onGoToProjectView = this.onGoToProjectView.bind(this);
    this.onKeyDownEditor = this.onKeyDownEditor.bind(this);
    this.onCloseInfoWindow = this.onCloseInfoWindow.bind(this);
    this.onOpenNewWindow = this.onOpenNewWindow.bind(this);
    this.onHoverProfilePicture = this.onHoverProfilePicture.bind(this);
    this.closeProfilePictureModal = this.closeProfilePictureModal.bind(this);
    this.onLeaveMiniaturePicture = this.onLeaveMiniaturePicture.bind(this);
    this.openChat = this.openChat.bind(this);
    this.onConverToProccess = this.onConverToProccess.bind(this);
  }

  onKeyDownEditor(e: React.KeyboardEvent<HTMLInputElement>): void
  {
    if (e.keyCode === 27)
    {
      this.setState({ enableEditBody: false, showBodyIsRequired: false });
    }
  }

  onGoToProjectView(e: React.MouseEvent<HTMLAnchorElement>): void
  {
    e.preventDefault();
    const { conversation } = this.props;

    // Check if there's a custom goToProject function (e.g., from chat)
    if (this.props.corgeeFunctions?.goToProject)
    {
      this.props.corgeeFunctions.goToProject(e as any, conversation);
      return;
    }

    // Default behavior for web app
    const path = `/ProjectDetails/${conversation.ProjectId}`;
    if (e.shiftKey || e.ctrlKey || e.metaKey)
    {
      window.open(window.location.origin + path, '_blank');
    }
    else
    {
      this.props.navigate?.(path);
      // Lets hosts like the Top Search popover close themselves
      this.props.corgeeFunctions?.onNavigateToProject?.(conversation.ProjectId ?? null);
    }
  }

  onChangeTitle(e: React.ChangeEvent<HTMLInputElement>): void
  {
    e.preventDefault();
    this.setState({ title: e.target.value });
  }

  onSaveTitle(e: React.MouseEvent | React.KeyboardEvent): void
  {
    e.preventDefault();

    if (!this.inputTitleRef) return;

    let newTitle = this.inputTitleRef.value;
    newTitle = '<p>' + newTitle + '</p>';
    newTitle = $(newTitle).text();

    const regexEmptyValue = /\S/;
    if (!regexEmptyValue.test(newTitle) || newTitle == null)
    {
      return;
    }

    this.setState({ title: newTitle, enableEditTitle: false, titleOriginal: newTitle }, () =>
    {
      this.enableInputClickEvent();
    });

    const requestParams: UpdateTitleRequest = {
      ConversationId: this.props.conversation.ConversationId,
      Title: newTitle,
      SocketConnectionId: this.props.SocketConnectionId,
      ConfigValueID: this.props.conversation.ConfigValueId,
    };

    if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarStart)
    {
      this.props.corgeeFunctions.loadingBarStart();
    }

    ConversationApi.updateTitle(this.props.api, requestParams)
      .then(response =>
      {
        this.setState({ updatedBy: response.UpdatedBy, updatedAt: response.UpdatedAt });

        if (this.props.onUpdateConversation)
        {
          const newAttrs: Partial<ConversationDetail> = {
            Title: newTitle,
            UpdatedBy: response.UpdatedBy,
            UpdatedAt: response.UpdatedAt,
          };
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            newAttrs,
            true,
            'header',
          );
        }

        if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
        {
          this.props.corgeeFunctions.loadingBarComplete();
        }

        ConversationApi.SetConversationsAsViewed(this.props.api, {
          ConversationIds: requestParams.ConversationId.toString(),
        }).then(() => {});
      })
      .catch(error =>
      {
        console.error(error);
      });
  }

  onSeeMoreContent(e: React.MouseEvent): void
  {
    e.preventDefault();
    this.setState({
      displayFullContent: !this.state.displayFullContent,
    });
  }

  onMarkAsRedFlag(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;
    const newRedFlag = !this.state.isRedFlag;

    this.setState({ isRedFlag: newRedFlag }, () =>
    {
      conversationBus.emit('redflag-updated', {
        conversationId: conversation.ConversationId,
        isRedFlag: newRedFlag,
      });

      if (newRedFlag === true)
      {
        $(`.spreadsheet-task-title[conversation-id="${conversation.ConversationId}"]`).css(
          'color',
          '#e14949',
        );
      }
      else if (newRedFlag === false)
      {
        $(`.spreadsheet-task-title[conversation-id="${conversation.ConversationId}"]`).css(
          'color',
          '',
        );
      }

      setTimeout(() =>
      {
        if (this.props.onUpdateConversation)
        {
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            { IsRedFlag: newRedFlag },
            true,
            'header',
          );
        }
      }, 0);

      const requestParams: UpdateRedFlagsRequest = {
        conversations: [
          {
            ConfigValueId: conversation.ConfigValueId,
            ConversationId: conversation.ConversationId,
            CoreTaskId: conversation.CoreTaskId,
            CreatedByID: conversation.CreatedByID,
            Finished: this.state.finished,
            Icon: conversation.Icon,
            IsRedFlag: !newRedFlag,
            ProjectId: conversation.ProjectId,
            SideMenuText: conversation.SideMenuText,
            Title: this.state.title,
            UpdatedAt: this.state.updatedAt,
            callService: true,
          },
        ],
        SocketConnectionId:
          this.props.SocketConnectionId !== undefined && this.props.SocketConnectionId !== null
            ? this.props.SocketConnectionId
            : '',
      };

      if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarStart)
      {
        this.props.corgeeFunctions.loadingBarStart();
      }

      ConversationApi.updateRedFlags(this.props.api, requestParams)
        .then(() =>
        {
          if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
          {
            this.props.corgeeFunctions.loadingBarComplete();
          }

          const conversationsIds = requestParams.conversations
            .map(obj => obj.ConversationId)
            .join(',');
          ConversationApi.SetConversationsAsViewed(this.props.api, {
            ConversationIds: conversationsIds,
          }).then(() => {});

          if (this.props.onUpdateConversation && this.props.userData)
          {
            const newAttrs: Partial<ConversationDetail> = {
              UpdatedBy: this.props.userData.personName,
              UpdatedAt: new Date().toISOString(),
            };
            this.props.onUpdateConversation(
              this.props.conversation.ConversationId,
              newAttrs,
              true,
              'header',
            );
          }
        })
        .catch(error =>
        {
          console.error(error);
        });
    });
  }

  onConverToProccess(response: ConversionResult[])
  {
    const strConversationsIds = response.map(c => c.ConversationId).join(',');
    const DashboardId = this.props.conversation.DashboardGroupId;
    const LayersToHide = CorgeeStorageHelper.getLayersToHide()?.join(',') || '0';
    ConversationApi.GetConversationsByIdsMultiedition({
      strConversationsIds,
      DashboardId,
      LayersToHide,
    }).then((response2: unknown) =>
    {
      const _r2 = response2 as ConversationDetail[];
      for (let i = 0; i < response.length; i++)
      {
        for (let j = 0; j < _r2.length; j++)
        {
          if (response[i]?.ConversationId === _r2[j]?.ConversationId)
          {
            const updated = _r2[j]!;
            const newIcon = updated.Icon || '';
            const updates: Partial<ConversationDetail> = {
              Icon: newIcon,
              ConfigValueId: updated.ConfigValueId,
              SideMenuText: updated.SideMenuText,
              _convertedAt: Date.now(),
            };
            // Only update local state for this component's own conversation
            if (updated.ConversationId === this.props.conversation.ConversationId)
            {
              this.setState({ icon: newIcon });
            }
            if (this.props.onUpdateConversation)
            {
              this.props.onUpdateConversation(
                response[i]?.ConversationId || 0,
                updates,
                true,
                'header',
              );
            }

            // onUpdateConversation only reaches this component's own host.
            conversationBus.emit('conversation-updated', {
              conversationId: response[i]?.ConversationId || 0,
              updates: { ...updates, refreshCustomFields: true },
            });
          }
        }
      }
    });
  }

  onMarkAsImportant(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;
    const newImportant = !this.state.isImportant;

    this.setState({ isImportant: newImportant }, () =>
    {
      setTimeout(() =>
      {
        if (this.props.onUpdateConversation)
        {
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            { IsImportant: newImportant },
            true,
            'header',
          );
        }
      }, 0);

      const requestParams: UpdateImportantRequest = {
        conversations: [
          {
            ConfigValueId: conversation.ConfigValueId,
            ConversationId: conversation.ConversationId,
            CoreTaskId: conversation.CoreTaskId,
            CreatedByID: conversation.CreatedByID,
            Finished: this.state.finished,
            Icon: conversation.Icon,
            IsImportant: !this.state.isImportant,
            ProjectId: conversation.ProjectId,
            SideMenuText: conversation.SideMenuText,
            Title: this.state.title,
            UpdatedAt: this.state.updatedAt,
            callService: true,
          },
        ],
        SocketConnectionId:
          this.props.SocketConnectionId !== undefined && this.props.SocketConnectionId !== null
            ? this.props.SocketConnectionId
            : '',
      };

      if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarStart)
      {
        this.props.corgeeFunctions.loadingBarStart();
      }

      ConversationApi.updateImportant(this.props.api, requestParams)
        .then(() =>
        {
          if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
          {
            this.props.corgeeFunctions.loadingBarComplete();
          }

          const conversationsIds = requestParams.conversations
            .map(obj => obj.ConversationId)
            .join(',');
          ConversationApi.SetConversationsAsViewed(this.props.api, {
            ConversationIds: conversationsIds,
          }).then(() => {});

          if (this.props.onUpdateConversation && this.props.userData)
          {
            const newAttrs: Partial<ConversationDetail> = {
              UpdatedBy: this.props.userData.personName,
              UpdatedAt: new Date().toISOString(),
            };
            this.props.onUpdateConversation(
              this.props.conversation.ConversationId,
              newAttrs,
              true,
              'header',
            );
          }
        })
        .catch(error =>
        {
          console.error(error);
        });
    });
  }

  onSendToBookmarks(e: React.MouseEvent): void
  {
    e.preventDefault();
    e.stopPropagation();

    const { conversation, api, SocketConnectionId } = this.props;
    const newIsFavorite = !this.state.isFavorite;

    // Update local state and trigger parent update
    this.setState({ isFavorite: newIsFavorite }, () =>
    {
      if (this.props.onUpdateConversation)
      {
        this.props.onUpdateConversation(
          conversation.ConversationId,
          { IsFavorite: newIsFavorite },
          true,
          'header',
        );
      }
    });

    // Execute endpoints
    const configValueId = CorgeeHelper.getBookmarksConfigValueId();
    ConversationApi.getDashboardsByConfigValueId(api, configValueId)
      .then((response: any) =>
      {
        const dashboards = response;
        if (dashboards && dashboards.length > 0)
        {
          const dashboardId = dashboards[0].DashboardId;
          const dashboardGroups = dashboards[0].DashboardGroups || [];
          const dashboardGroupIdSend =
            dashboardGroups.length > 0 ? dashboardGroups[0].DashboardGroupId : 0;

          if (newIsFavorite)
          {
            const params = {
              dashboardId,
              conversationId: conversation.ConversationId,
              dashboardGroupIdSend,
              includeAllConversations: true,
              socketid: SocketConnectionId || '',
              isBookmark: newIsFavorite,
            };

            ConversationApi.addDashboardGroupByParameters(api, params);
          }
          else
          {
            const params = {
              dashboardId,
              conversationId: conversation.ConversationId,
              dashboardGroupIdSend,
              DashboardGroupIdDefault: 0,
              pParentId: conversation.ParentConversationId
                ? conversation.ParentConversationId
                : conversation.ConversationId,
              pDashboardGroupId: dashboardGroupIdSend,
              IsFromBookmark: true,
              layersToHide: CorgeeStorageHelper.getLayersToHide()?.join(',') || '0',
              socketid: SocketConnectionId ? SocketConnectionId : '',
            };

            ConversationApi.DeleteDashboardGroupConversation(api, params);
          }
        }
        return Promise.resolve();
      })
      .then(() =>
      {
        const params = {
          conversationId: conversation.ConversationId,
          socketid: SocketConnectionId || '',
        };
        ConversationApi.notifIsFavoriteExternal(api, params);
      })
      .then(() =>
      {
        ConversationApi.SetConversationsAsViewed(api, {
          ConversationIds: conversation.ConversationId.toString(),
        });
      })
      .catch(error => console.error('Error updating bookmark:', error));
  }

  updateUpdatedBy(conversationId: number): void
  {
    const requestParamsConv = {
      conversationId: conversationId,
      layersToHide: '',
      getAllReplies: false,
      isConversationDetailsView: true,
      totalReplies: 0,
    };

    ConversationApi.getConversationById(this.props.api, requestParamsConv).then(
      (conv: ConversationDetail) =>
      {
        if (this.props.onUpdateConversation)
        {
          const newAttrs: Partial<ConversationDetail> = {
            UpdatedBy: conv.UpdatedBy,
            UpdatedAt: new Date().toISOString(),
          };
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            newAttrs,
            true,
            'header',
          );
        }
      },
    );
  }

  getClickHandler(
    onClick: (event: Event) => void,
    onDblClick: (event: Event) => void,
    delay: number = 250,
  ): (event: Event) => void
  {
    let timeoutID: ReturnType<typeof setTimeout> | null = null;

    return (event: Event) =>
    {
      // event.detail is set by the browser itself (2 on the second click of
      // a double-click), so this doesn't depend on JS-side timing at all -
      // more reliable than only comparing elapsed time via setTimeout,
      // especially under a chat app's heavier real-time event load (a
      // socket-driven re-render can delay a queued setTimeout callback,
      // causing it to fire the single-click action right as the second
      // click arrives and silently swallowing the double-click).
      const detail = (event as MouseEvent).detail;

      if (detail && detail >= 2)
      {
        if (timeoutID)
        {
          clearTimeout(timeoutID);
          timeoutID = null;
        }
        onDblClick(event);
        return;
      }

      if (!timeoutID)
      {
        timeoutID = setTimeout(() =>
        {
          onClick(event);
          timeoutID = null;
        }, delay);
      }
    };
  }

  onEditTitle(e: React.KeyboardEvent<HTMLInputElement>): boolean
  {
    e.preventDefault();

    if (e.keyCode)
    {
      if (e.keyCode === 27)
      {
        this.setState({ enableEditTitle: false, title: this.state.titleOriginal }, () =>
        {
          this.enableInputClickEvent();
        });
        return false;
      }
      else if (e.keyCode !== 13)
      {
        return false;
      }
    }

    this.onSaveTitle(e);
    return true;
  }

  onChangeFinished(e: React.ChangeEvent<HTMLInputElement>): void
  {
    const checked = e.target.checked;
    this.setState({ finished: checked });

    conversationBus.emit('completed-updated', {
      conversationId: this.props.conversation.ConversationId,
      isCompleted: checked,
    });

    let dashboardgroupid = this.props.conversation.DashboardGroupId;

    if (
      this.props.conversation.RelatedConversationId &&
      this.props.conversation.RelatedConversationId > 0
    )
    {
      const $elem = $(
        `.liConversationTitle.liConversationItem[data-conversationid='${this.props.conversation.RelatedConversationId}']`,
      );
      if ($elem.length > 0)
      {
        dashboardgroupid = $elem.data('groupid') || dashboardgroupid;
      }
    }

    const requestParams: UpdateCompleteRequest = {
      DashboardGroupId: dashboardgroupid || this.props.conversation.DashboardGroupId,
      ConversationId: this.props.conversation.ConversationId,
      Finished: checked,
      SocketConnectionId:
        this.props.SocketConnectionId !== undefined && this.props.SocketConnectionId !== null
          ? this.props.SocketConnectionId
          : '',
    };

    if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarStart)
    {
      this.props.corgeeFunctions.loadingBarStart();
    }

    ConversationApi.updateComplete(this.props.api, requestParams)
      .then(response =>
      {
        if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
        {
          this.props.corgeeFunctions.loadingBarComplete();
        }

        if (response !== false && this.props.corgeeFunctions?.callBackUpdateComplete)
        {
          this.props.corgeeFunctions.callBackUpdateComplete(response);
        }

        ConversationApi.SetConversationsAsViewed(this.props.api, {
          ConversationIds: requestParams.ConversationId.toString(),
        }).then(() => {});
        this.updateUpdatedBy(this.props.conversation.ConversationId);
      })
      .catch(error =>
      {
        console.error('Error updating complete status:', error);
        if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
        {
          this.props.corgeeFunctions.loadingBarComplete();
        }
      });

    this.setState({ finished: checked }, () =>
    {
      if (this.props.onUpdateConversation)
      {
        this.props.onUpdateConversation(
          this.props.conversation.ConversationId,
          { Finished: this.state.finished },
          true,
          'header',
        );
      }
    });
  }

  enableInputClickEvent(): void
  {
    if (!this.spanTitleRef) return;

    if (this._titleClickHandler)
    {
      this.spanTitleRef.removeEventListener('click', this._titleClickHandler);
      this._titleClickHandler = null;
    }

    this._titleClickHandler = this.getClickHandler(
      (e: Event) =>
      {
        e.preventDefault();
        if (
          this.props.corgeeFunctions &&
          this.props.corgeeFunctions.openInfoWindow &&
          !this.props.isConversationDetailsView
        )
        {
          this.props.corgeeFunctions.openInfoWindow(this.props.conversation);
        }
      },
      (e: Event) =>
      {
        e.preventDefault();
        if (CorgeeHelper.convHasTitleEditPermissions(this.props.conversation))
        {
          this.setState({ enableEditTitle: true });
        }
      },
      400,
    ) as EventListener;

    this.spanTitleRef.addEventListener('click', this._titleClickHandler);
  }

  componentDidMount(): void
  {
    this.enableInputClickEvent();

    this._unsubRedflag = conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
    {
      if (conversationId === this.props.conversation.ConversationId)
      {
        this.setState({ isRedFlag });
      }
    });

    this._unsubCompleted = conversationBus.on('completed-updated', ({ conversationId, isCompleted }) =>
    {
      if (conversationId === this.props.conversation.ConversationId)
      {
        this.setState({ finished: isCompleted });
      }
    });

    if (this.props.storageData && this.props.storageData.enableChat === true)
    {
      const reqNewChat: NewChatRequest = {
        Participant1:
          this.props.storageData && this.props.storageData.personid
            ? this.props.storageData.personid
            : 0,
        Participant2: this.props.reply
          ? this.props.reply.CreatedById
          : this.props.conversation.PersonId || 0,
        ChatID: 0,
      };

      if (
        reqNewChat.Participant1 > 0 &&
        reqNewChat.Participant2 > 0 &&
        reqNewChat.Participant1 !== reqNewChat.Participant2
      )
      {
        this.setState({ canOpenChat: true, reqNewChat: reqNewChat });
      }
    }

    if (this.props.chatData && this.props.chatData.enableChat === true)
    {
      const reqNewChat: NewChatRequest = {
        Participant1:
          this.props.chatData && this.props.chatData.personid ? this.props.chatData.personid : 0,
        Participant2: this.props.reply
          ? this.props.reply.CreatedById
          : this.props.conversation.PersonId || 0,
        ChatID: 0,
      };

      if (
        reqNewChat.Participant1 > 0 &&
        reqNewChat.Participant2 > 0 &&
        parseInt(reqNewChat.Participant1.toString()) !==
          parseInt(reqNewChat.Participant2.toString())
      )
      {
        this.setState({ canOpenChat: true, reqNewChat: reqNewChat });
      }
    }
  }

  componentWillUnmount(): void
  {
    if (this.spanTitleRef && this._titleClickHandler)
    {
      this.spanTitleRef.removeEventListener('click', this._titleClickHandler);
      this._titleClickHandler = null;
    }
    if (this._unsubRedflag)
    {
      this._unsubRedflag();
      this._unsubRedflag = null;
    }
    if (this._unsubCompleted)
    {
      this._unsubCompleted();
      this._unsubCompleted = null;
    }
  }

  componentDidUpdate(prevProps: ConversationCardHeaderProps): void
  {
    if (this.props.conversation && prevProps.conversation)
    {
      // Sync finished state when prop changes
      if (
        this.props.conversation.Finished !== prevProps.conversation.Finished &&
        this.props.conversation.Finished !== this.state.finished
      )
      {
        this.setState({ finished: this.props.conversation.Finished });
      }

      // Sync title state when prop changes
      if (
        this.props.conversation.Title !== prevProps.conversation.Title &&
        this.props.conversation.Title !== this.state.title
      )
      {
        this.setState({
          title: this.props.conversation.Title,
          titleOriginal: this.props.conversation.Title,
        });
      }

      // Sync updatedAt/updatedBy when prop changes
      if (
        this.props.conversation.UpdatedAt !== prevProps.conversation.UpdatedAt &&
        this.props.conversation.UpdatedAt !== this.state.updatedAt
      )
      {
        this.setState({
          updatedBy: this.props.conversation.UpdatedBy,
          updatedAt: this.props.conversation.UpdatedAt,
        });
      }

      // Sync isRedFlag when prop changes
      if (
        this.props.conversation.IsRedFlag !== prevProps.conversation.IsRedFlag &&
        this.props.conversation.IsRedFlag !== this.state.isRedFlag
      )
      {
        this.setState({ isRedFlag: this.props.conversation.IsRedFlag });
      }

      // Sync isImportant when prop changes
      if (
        this.props.conversation.IsImportant !== prevProps.conversation.IsImportant &&
        this.props.conversation.IsImportant !== this.state.isImportant
      )
      {
        this.setState({ isImportant: this.props.conversation.IsImportant });
      }

      // Sync body when prop changes
      if (
        this.props.conversation.Body !== prevProps.conversation.Body &&
        this.props.conversation.Body !== this.state.body
      )
      {
        this.setState({ body: this.props.conversation.Body });
      }

      // Sync firstLayerColor when ConversationLayers change
      if (
        this.props.conversation.ConversationLayers &&
        this.props.conversation.ConversationLayers.length > 0
      )
      {
        let layers = this.props.conversation.ConversationLayers;
        layers = layers.sort((a, b) => (a.PriorityInConfigValue || 0) - (b.PriorityInConfigValue || 0));
        layers = layers.filter(x => x.LayerId !== 0);
        const firstLayerColor = layers.length > 0 ? layers[0]?.LayerColor || '' : '';

        if (firstLayerColor !== this.state.firstLayerColor)
        {
          this.setState({ firstLayerColor: firstLayerColor });
        }
      }

      // Sync isFavorite when prop changes
      if (
        this.props.conversation.IsFavorite !== prevProps.conversation.IsFavorite &&
        this.props.conversation.IsFavorite !== this.state.isFavorite
      )
      {
        this.setState({ isFavorite: this.props.conversation.IsFavorite || false });
      }
    }
  }

  /**
   * Copia el texto al portapapeles usando la API moderna de Clipboard
   * con fallback para navegadores antiguos
   */
  async copyToClipboard(text: string): Promise<void>
  {
    try
    {
      // Intentar usar la API moderna de Clipboard
      await navigator.clipboard.writeText(text);
      this.onCopyToClipboard(text, true);
    }
    catch (err)
    {
      // Fallback para navegadores que no soportan la API de Clipboard
      try
      {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        this.onCopyToClipboard(text, successful);
      }
      catch (fallbackErr)
      {
        console.error('Failed to copy text:', fallbackErr);
        this.onCopyToClipboard(text, false);
      }
    }
  }

  onCopyToClipboard(text: string, result: boolean): void
  {
    if (!result)
    {
      console.error('Failed to copy to clipboard');
      return;
    }

    this.setState({ linkCopied: true }, () =>
    {
      setTimeout(() =>
      {
        $('.corgee_com_notification-bubble-container.clipboard-notification-r')
          .fadeIn()
          .delay(1000)
          .fadeOut(3000);

        if (this.props.corgeeFunctions && this.props.corgeeFunctions.onCopyToClipboard)
        {
          this.props.corgeeFunctions.onCopyToClipboard(text);
        }

        setTimeout(() =>
        {
          this.setState({ linkCopied: false });
        }, 4000);
      }, 0);
    });
  }

  onCloseInfoWindow(): void
  {
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.onCloseInfoWindow)
    {
      this.props.corgeeFunctions.onCloseInfoWindow();
    }
  }

  onOpenNewWindow(): void
  {
    const { conversation } = this.props;

    // Check if there's a custom onOpenNewWindow function (e.g., from chat)
    if (this.props.corgeeFunctions?.onOpenNewWindow)
    {
      this.props.corgeeFunctions.onOpenNewWindow(conversation);
      return;
    }

    // Default behavior for web app
    window.open(
      window.location.origin +
        import.meta.env.BASE_URL +
        'Dashboard/ConversationDetails/#/' +
        conversation.ConversationId,
      '_blank',
    );
  }

  onHoverProfilePicture(e: React.MouseEvent<HTMLSpanElement>): void
  {
    // If a host app (e.g. Chat) provides its own profile hover, delegate to it
    // instead of opening the web ProfilePictureModal.
    if (this.props.corgeeFunctions?.onHoverProfilePicture)
    {
      const personId = this.props.conversation?.PersonId;
      if (personId)
      {
        this.props.corgeeFunctions.onHoverProfilePicture(e, personId);
      }
      return;
    }

    let positionY = e.currentTarget.getBoundingClientRect().y;
    if (positionY < 50) positionY += 75;
    let positionX = e.currentTarget.getBoundingClientRect().x - 40;
    e.preventDefault();

    const dm = storage.getItem('enableDarkMode') === 'true';
    const _div = document.getElementsByClassName('corgee_com_profile-picture-modal-outside')[0];

    if (!_div)
    {
      if (!dm)
      {
        $('.see-all-replies').blur();
        $('.corgee_com_card-hide-details-btn').blur();
        $('.corgee_com_card-see-more-btn').blur();
      }

      if (window.innerHeight - positionY < 145)
      {
        positionY = positionY - 165;
      }

      if (window.innerWidth - positionX < 450)
      {
        positionX = positionX - 470;
      }

      this.setState({
        openProfilePictureDialog: true,
        positionProfilePictureY: positionY,
        positionProfilePictureX: positionX,
      });
    }
  }

  closeProfilePictureModal(e?: React.MouseEvent): void
  {
    if (e)
    {
      e.preventDefault();
    }
    this.setState({ openProfilePictureDialog: false });
  }

  onLeaveMiniaturePicture(): void
  {
    // Delegate to host app's profile hover close handler when provided (e.g. Chat).
    if (this.props.corgeeFunctions?.onLeaveProfilePicture)
    {
      this.props.corgeeFunctions.onLeaveProfilePicture();
      return;
    }

    if (this.profilePictureItem && this.profilePictureItem.closeFromOutSide)
    {
      this.profilePictureItem.closeFromOutSide();
    }
  }

  openChat(personId: number): void
  {
    if (this.props.corgeeFunctions && this.props.corgeeFunctions.openChat)
    {
      this.props.corgeeFunctions.openChat(personId);
    }
    else
    {
      const { canOpenChat, reqNewChat } = this.state;

      if (canOpenChat === true)
      {
        void this.props.conversation.ProjectId;
        void this.props.conversation.UpdatedAt;
        void (this.props.conversation.ProjectName
          ? this.props.conversation.ProjectName
          : this.state.title);
        const urlChat = getChatBaseUrl();

        try
        {
          if (window.chatWebReact !== null && window.chatWebReact !== undefined)
          {
            if (
              window.chatWebReact.location !== null &&
              window.chatWebReact.location !== undefined &&
              window.chatWebReact.location.origin + window.chatWebReact.location.pathname ===
                urlChat &&
              window.chatWebReact.closed === false
            )
            {
              window.chatWebReact.focus();
            }
          }
        }
        catch (e)
        {
          // Ignore error
        }

        const isSafari =
          navigator.userAgent.indexOf('Safari') !== -1 &&
          navigator.userAgent.indexOf('Chrome') === -1;
        let windowReferenceSafari: Window | null = null;

        if (isSafari)
        {
          windowReferenceSafari = window.open();
        }

        ConversationApi.createNewPrivateChat(this.props.api, reqNewChat as NewChatRequest).then(
          (chatResponse: ChatResponse) =>
          {
            const ChatId = chatResponse.ChatID !== undefined ? chatResponse.ChatID : 0;

            if (isSafari && windowReferenceSafari)
            {
              windowReferenceSafari.location.href = buildChatWindowUrl(ChatId);
              return;
            }

            try
            {
              if (window.chatWebReact !== null && window.chatWebReact !== undefined)
              {
                if (
                  window.chatWebReact.location !== null &&
                  window.chatWebReact.location !== undefined &&
                  window.chatWebReact.location.origin + window.chatWebReact.location.pathname ===
                    urlChat &&
                  window.chatWebReact.closed === false
                )
                {
                  window.chatWebReact =
                    window.open(buildChatWindowUrl(ChatId), 'chatWebReact') || null;
                  setTimeout(() =>
                  {
                    if (window.chatWebReact)
                    {
                      window.chatWebReact.location.reload();
                    }
                  }, 200);
                }
                else
                {
                  window.chatWebReact =
                    window.open(buildChatWindowUrl(ChatId), 'chatWebReact') || null;
                }
              }
              else
              {
                window.chatWebReact =
                  window.open(buildChatWindowUrl(ChatId), 'chatWebReact') || null;
                if (window.chatWebReact)
                {
                  window.chatWebReact.focus();
                }
              }
            }
            catch (e)
            {
              window.chatWebReact = null;

              const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
              if (isFirefox)
              {
                setTimeout(() =>
                {
                  window.chatWebReact =
                    window.open(buildChatWindowUrl(ChatId), 'chatWebReact') || null;
                  if (window.chatWebReact)
                  {
                    window.chatWebReact.focus();
                  }
                }, 1000);
              }
              else
              {
                window.chatWebReact =
                  window.open(buildChatWindowUrl(ChatId), 'chatWebReact') || null;
              }
            }
          },
        );
      }
    }
  }

  render(): React.ReactElement
  {
    const {
      conversation,
      api,
      isConversationDetailsView,
      isInfowindowDrag,
      isInfowindowViewCard,
      config: _config,
      onHighlight,
    } = this.props;
    const {
      linkCopied,
      openProfilePictureDialog,
      finished,
      title,
      updatedBy,
      updatedAt,
      enableEditTitle,
      isRedFlag,
      displayFullContent: _displayFullContent,
      firstLayerColor,
      positionProfilePictureX,
      positionProfilePictureY,
      isImportant,
      icon,
      isGroupDefault,
      isFavorite,
      isRecycleBin,
    } = this.state;

    let infoWindowDragHandlers: React.HTMLAttributes<HTMLElement> = {};
    if (isInfowindowDrag)
    {
      const buildGhost = (): HTMLElement =>
      {
        const ghost = document.createElement('div');
        ghost.style.cssText = [
          'position:fixed',
          'top:0',
          'left:-9999px',
          'display:flex',
          'align-items:center',
          'gap:6px',
          'padding:6px 12px 6px 8px',
          'background:#fff',
          'border:1px solid #d0d0d0',
          'border-radius:4px',
          'box-shadow:0 2px 8px rgba(0,0,0,0.18)',
          'font-family:Roboto,sans-serif',
          'font-size:13px',
          'color:#333',
          'width:300px',
          'pointer-events:none',
          'z-index:99999',
          'white-space:nowrap',
          'overflow:hidden',
        ].join(';');

        const iconEl = document.createElement('span');
        iconEl.className = `identifier-icon-key ${conversation.Icon || 'icon-infinite-icon'}`;
        iconEl.style.cssText = 'font-size:14px;flex-shrink:0;color:#888;';
        ghost.appendChild(iconEl);

        const checkEl = document.createElement('span');
        checkEl.className = 'material-icons';
        checkEl.textContent = conversation.Finished ? 'check_circle' : 'check_circle_outline';
        checkEl.style.cssText = `font-size:14px;flex-shrink:0;color:${conversation.Finished ? '#62aacb' : '#ccc'};`;
        ghost.appendChild(checkEl);

        const titleEl = document.createElement('span');
        titleEl.textContent = conversation.Title || 'Card';
        titleEl.style.cssText =
          'flex:1;overflow:hidden;text-overflow:ellipsis;font-size:13px;color:#333;min-width:0;';
        ghost.appendChild(titleEl);

        const rightText =
          conversation.ConversationLayers?.[0]?.LayerName || conversation.ProjectName || '';
        if (rightText)
        {
          const rightEl = document.createElement('span');
          rightEl.textContent = rightText.length > 16 ? rightText.slice(0, 15) + '…' : rightText;
          rightEl.style.cssText = 'font-size:11px;color:#999;flex-shrink:0;margin-left:4px;';
          ghost.appendChild(rightEl);
        }
        return ghost;
      };

      const handleInfoWindowDragStart = (e: React.DragEvent) =>
      {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'copyMove';
        setCardBeingDragged({
          conversationId: conversation.ConversationId,
          groupId: 0,
          dashboardId: getCurrentDashboardId(),
          conversation,
        });
        // Build ghost and keep it in DOM until dragend
        const ghost = buildGhost();
        document.body.appendChild(ghost);
        this._dragGhost = ghost;
        e.dataTransfer.setDragImage(ghost, 150, 16);
      };

      const handleInfoWindowDragEnd = (e: React.DragEvent) =>
      {
        e.stopPropagation();
        setCardBeingDragged(null);
        if (this._dragGhost)
        {
          this._dragGhost.remove();
          this._dragGhost = null;
        }
      };

      infoWindowDragHandlers = {
        draggable: true,
        onDragStart: handleInfoWindowDragStart,
        onDragEnd: handleInfoWindowDragEnd,
      };
    }

    const cssClass = {
      RedFlag: 'corgee-icon-red-flag enable-help-tooltip',
      TaskTitle: 'corgee_com_card-header-content-task-title-name enable-help-tooltip',
    };

    const canRenameTitle = CorgeeHelper.convHasTitleEditPermissions(conversation);
    const tooltipText = {
      CheckBox: 'Mark as Completed',
      RedFlag: 'Mark as Redflag',
      TaskTitle: isConversationDetailsView
        ? canRenameTitle
          ? 'Title of the card. Double click to rename.'
          : 'Title of the card.'
        : canRenameTitle
          ? 'Title of the card. Click to open Info Window. Double click to rename.'
          : 'Title of the card. Click to open Info Window.',
    };

    let layoutClass = '';
    if (conversation.ConfigValueId === 15)
    {
      layoutClass = 'card-with-image';
    }
    else
    {
      layoutClass = 'card-without-image';
    }

    layoutClass =
      conversation.ConfigValueId !== 15
        ? layoutClass + ' card-has-checkbox'
        : layoutClass + ' card-without-checkbox';

    const iconCssClass = icon
      ? `identifier-icon-key ${icon} enable-help-tooltip`
      : 'identifier-icon-key icon-infinite-icon enable-help-tooltip';

    const iconCardItemName =
      this.props.conversation.SideMenuText && this.props.conversation.SideMenuText.length > 0
        ? this.props.conversation.SideMenuText + ' Card'
        : '';

    if (finished)
    {
      tooltipText.CheckBox = 'Unmark as Completed';
    }

    if (isRedFlag)
    {
      tooltipText.RedFlag = 'Unmark as Redflag';
      cssClass.RedFlag += ' red';
      cssClass.TaskTitle += ' red';
    }

    if (!conversation.IsEnable)
    {
      tooltipText.CheckBox = 'Deleted items cannot change their complete status';
      tooltipText.RedFlag = 'Deleted items cannot change their red flag status';
      tooltipText.TaskTitle = 'Title of the card. Click to open Info Window.';
    }

    void CorgeeHelper.convHasEditPermissions(conversation, null);

    let enableEditTitleValidated = enableEditTitle;
    if (conversation.ConfigValueId === 15)
    {
      enableEditTitleValidated = false;
    }

    // In host apps (Chat) getBaseUrl() is the full web base; don't append BASE_URL (the
    // host's own /chatv2/). Web falls back to window.location.origin + BASE_URL.
    const customBaseUrl = this.props.corgeeFunctions?.getBaseUrl?.();
    const clipboardUrl = customBaseUrl
      ? customBaseUrl.replace(/\/+$/, '') +
        '/Dashboard/ConversationDetails/#/' +
        conversation.ConversationId
      : window.location.origin +
        import.meta.env.BASE_URL +
        'Dashboard/ConversationDetails/#/' +
        conversation.ConversationId;

    const isNarrowLayout = this.props.columnWidth !== undefined && this.props.columnWidth < 335;

    const shouldShowProjectName =
      !isNarrowLayout &&
      !this.props.hideProjectName &&
      conversation.ProjectId &&
      conversation.ConfigValueId;

    return (
      <div
        className={
          onHighlight ? 'corgee_com_card-header notification-highlight' : 'corgee_com_card-header'
        }
      >
        <div
          className="corgee_com_card-header-status-bar"
          {...infoWindowDragHandlers}
          style={isInfowindowDrag ? { cursor: 'grab' } : undefined}
        >
          {/* Card Header Left Section */}
          <div className={'corgee_com_card-status-bar-layout-type ' + layoutClass}>
            {/* Layers Section */}
            <div
              className="corgee_com_card-header-layers enable-help-tooltip layers-help-tooltip"
              data-title="Layers"
            >
              {conversation.ConversationLayers &&
                conversation.ConversationLayers.sort((a, b) =>
                  (a.PriorityInConfigValue || 0) - (b.PriorityInConfigValue || 0),
                ).map(layer =>
                  layer.LayerId !== 0 ? (
                    <span
                      key={'cl-' + layer.ConversationId + '-' + layer.LayerId}
                      className="corgee_com_card-layer"
                      style={{ backgroundColor: layer.LayerColor ? layer.LayerColor : layer.Color ?  layer.Color : '' }}
                    ></span>
                  ) : null,
                )}
            </div>

            {/* Icons Section */}
            <div className="corgee_com_card-header-icons">
              <span className={iconCssClass} data-title={iconCardItemName}></span>
              {conversation.ConfigValueId === 15 && (
                <span
                  className="user-picture-image-conversation infoWindow-picture"
                  style={{ backgroundImage: `url(${conversation.ImageName})` }}
                  onMouseEnter={this.onHoverProfilePicture}
                  onMouseLeave={this.onLeaveMiniaturePicture}
                ></span>
              )}
              {/* Checkbox — hidden when card is a parent (IsParent) in dashboard view. In InfoWindow always visible. */}
              {conversation.ConfigValueId !== 1 &&
                conversation.ConfigValueId !== 9 &&
                conversation.ConfigValueId !== 15 &&
                (!conversation.IsParent || isInfowindowViewCard) && (
                <span className="corgee_com_checkbox">
                  {conversation.IsEnable ? (
                    <input
                      id={`taskCardCheckbox-react-${conversation.ConversationId}`}
                      className="taskCardCheckbox-react"
                      type="checkbox"
                      checked={finished}
                      onChange={this.onChangeFinished}
                    />
                  ) : (
                    <input
                      id={`taskCardCheckbox-react-${conversation.ConversationId}`}
                      className="taskCardCheckbox-react"
                      type="checkbox"
                      checked={finished}
                      disabled
                    />
                  )}
                  <label
                    htmlFor={`taskCardCheckbox-react-${conversation.ConversationId}`}
                    className="conversationCheckbox-react enable-help-tooltip"
                    data-title={tooltipText.CheckBox}
                  ></label>
                </span>
              )}
              {/* Progress pill — only for parent cards in dashboard view (not in InfoWindow) */}
              {conversation.IsParent === true &&
                !isInfowindowViewCard &&
                conversation.ConfigValueId !== 1 &&
                conversation.ConfigValueId !== 9 &&
                conversation.ConfigValueId !== 15 && (
                <span
                  className="corgee_com_card-progress-pill enable-help-tooltip"
                  data-title={`Progress: ${Math.round(conversation.Progress || 0)}%`}
                >
                  <span
                    className="corgee_com_card-progress-pill-fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, conversation.Progress || 0))}%`,
                    }}
                  />
                </span>
              )}
              {/* Important Section */}
              {isImportant && (
                <div className="div-content-important">
                  <span className="corgee_important_mark">IMPORTANT</span>
                </div>
              )}
            </div>
          </div>

          <ProfilePictureModal
            {...(this.props as any)}
            ref={(element: any) =>
            {
              this.profilePictureItem = element;
            }}
            closeProfilePictureModal={this.closeProfilePictureModal}
            openProfilePictureDialog={openProfilePictureDialog}
            api={api}
            conversation={conversation}
            positionX={positionProfilePictureX}
            positionY={positionProfilePictureY}
            canOpenChat={true}
            canOpenCard={true}
            openNewWindow={true}
            openChat={this.openChat}
          />

          {/* Card Header Right Section */}
          <div className="corgee_com_card-header-status-bar-tools">
            {shouldShowProjectName && (
              <div className="corgee_com_card-header-visible-icons">
                <a
                  className={`corgee_com_card-header-project-name go-to-project ${conversation.ProjectIsEnable ? '' : 'no-click-project'}`}
                  onClick={conversation.ProjectIsEnable ? this.onGoToProjectView : undefined}
                >
                  {conversation.ConfigValueId !== 9 ? (
                    conversation.ProjectName ? (
                      conversation.ProjectName
                    ) : (
                      title
                    )
                  ) : (
                    <i className="fa fa-folder-open-o"></i>
                  )}
                </a>
              </div>
            )}
            <div className="corgee_com_card-header-actions">
              {shouldShowProjectName && (
                <div
                  className={`corgee_com_card-header-project-name-container go-to-project ${conversation.ProjectIsEnable ? 'enable-help-tooltip' : 'no-click-project'}`}
                  data-title="Click to go to project view"
                >
                  <a
                    className={`corgee_com_card-header-project-name go-to-project ${conversation.ProjectIsEnable ? '' : 'no-click-project'}`}
                    onClick={conversation.ProjectIsEnable ? this.onGoToProjectView : undefined}
                  >
                    {conversation.ConfigValueId !== 9 ? (
                      conversation.ProjectName ? (
                        conversation.ProjectName
                      ) : (
                        title
                      )
                    ) : (
                      <i className="fa fa-folder-open-o"></i>
                    )}
                  </a>
                </div>
              )}
              {!this.props.isHomeView && !enableEditTitleValidated && !isRecycleBin && (
                <div
                  className={`title-card-action-icon bookmark ${isFavorite ? 'favorite' : ''}`}
                  onClick={this.onSendToBookmarks}
                >
                  <i
                    className="material-icons h-5"
                    data-conversationid={conversation.ConversationId}
                  >
                    {isFavorite ? 'bookmark' : 'bookmark_border'}
                  </i>
                </div>
              )}
            </div>
            {this.props.corgeeFunctions &&
              this.props.corgeeFunctions.onOpenNewWindow &&
              this.props.isInfowindowViewCard && (
              <div
                className="corgee_com_card-header-action-icon open-new-window-infowindow"
                onClick={this.onOpenNewWindow}
              >
                <span className="material-icons">open_in_new</span>
              </div>
            )}
            {conversation.ConversationId && !isNarrowLayout && (
              <div
                className="corgee_com_card-header-action-icon copy-to-clipboard-action-icon enable-help-tooltip"
                data-title="Copy to clipboard"
                onClick={() => this.copyToClipboard(clipboardUrl)}
                style={{ cursor: 'pointer' }}
              >
                <span className="material-icons">link</span>
              </div>
            )}
            {this.props.corgeeFunctions &&
              this.props.corgeeFunctions.onCloseInfoWindow &&
              this.props.isInfowindowViewCard && (
              <div className="corgee_com_card-header-action-icon close-infowindow">
                <span
                  style={{ zIndex: 10 }}
                  className="icon-incorrect-icon"
                  onClick={this.onCloseInfoWindow}
                ></span>
              </div>
            )}
            <div className="corgee_com_card-header-menu">
              <ActionMenuConversation
                {...(this.props as any)}
                isRedFlag={isRedFlag}
                isImportant={isImportant ?? undefined}
                isLoadingMenu={true}
                onMarkAsRedFlag={this.onMarkAsRedFlag}
                onMarkAsImportant={this.onMarkAsImportant}
                onConverToProccess={this.onConverToProccess as any}
                onSubmitTime={this.props.onSubmitTime}
                onAddReply={this.props.onAddReply as any}
                isGroupDefawult={isGroupDefault}
              />
            </div>
          </div>
        </div>

        {/* Card Header Content */}
        <div className="corgee_com_card-header-content">
          <div className="corgee_com_card-header-content-description cancel-drag">
            <div className="corgee_com_card-header-content-task-title cancel-drag">
              <div className="corgee_com_card-header-content-task-editable">
                {enableEditTitleValidated === false && (
                  <span
                    ref={element =>
                    {
                      this.spanTitleRef = element;
                    }}
                    className={cssClass.TaskTitle}
                    data-title={tooltipText.TaskTitle}
                  >
                    {title}
                  </span>
                )}
                {enableEditTitleValidated === true && (
                  <input
                    className="corgee_com_input"
                    type="text"
                    ref={element =>
                    {
                      this.inputTitleRef = element;
                    }}
                    autoFocus
                    defaultValue={title}
                    onKeyUp={this.onEditTitle}
                    onFocus={e =>
                    {
                      const len = e.target.value.length;
                      e.target.setSelectionRange(len, len);
                      e.target.scrollLeft = e.target.scrollWidth;
                    }}
                  />
                )}
                {enableEditTitleValidated === true && (
                  <button
                    type="button"
                    style={{
                      display: 'inline-block',
                      backgroundColor: firstLayerColor,
                      borderColor: firstLayerColor,
                    }}
                    onClick={this.onSaveTitle}
                    className="corgee_com_button corgee_com_sm-button btnChangeColorWithLayer"
                  >
                    Save
                  </button>
                )}
              </div>
              <span className="corgee_com_card-author">
                <span>
                  Posted by {conversation.CreatedBy} -{' '}
                  <span
                    className="timestamp enable-help-tooltip"
                    data-title={CorgeeHelper.applyFormatDate(
                      conversation.CreatedAt,
                      'MMM[.] DD[,] YYYY [at] hh:mm A',
                    )}
                  >
                    {CorgeeHelper.applyFormatDate(
                      conversation.CreatedAt,
                      'MMM[.] DD[,] YYYY [at] hh:mm A',
                    )}
                  </span>
                </span>
                {updatedBy && updatedBy.length > 0 && (
                  <span className="updated-by">
                    <i>
                      &nbsp; Updated by {updatedBy} -{' '}
                      <span
                        className="timestamp enable-help-tooltip"
                        data-title={CorgeeHelper.applyFormatDate(
                          updatedAt,
                          'MMM[.] DD[,] YYYY [at] hh:mm A',
                        )}
                      >
                        {CorgeeHelper.applyFormatDate(updatedAt, 'MMM[.] DD[,] YYYY [at] hh:mm A')}
                      </span>
                    </i>
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        {linkCopied && (
          <RenderInBody>
            <div className="corgee_com_notification-bubble-container clipboard-notification-r">
              <div className="corgee_com_notification-bubble">
                Link has been copied to clipboard.
              </div>
            </div>
          </RenderInBody>
        )}
      </div>
    );
  }
}

function ConversationCardHeaderWrapper(props: Omit<ConversationCardHeaderProps, 'navigate'>)
{
  const navigate = useNavigate();
  return <ConversationCardHeader {...props} navigate={navigate} />;
}

export default ConversationCardHeaderWrapper;
